import React, { useRef, useState } from 'react';
import { IconButton, iconButtonClasses, Stack, Typography, StackProps, useTheme } from '@mui/material';
import { copyTextToClipboard, OnResize, selectElementContent } from 'utils';
import { ContentCopy } from '@mui/icons-material';
import { StyledTooltip } from 'components/styled';
import { useElementResized } from 'hooks';

type WebsiteRowProps = {
  url: string;
  showProtocol?: boolean;
} & Pick<StackProps, 'component'>;

const urlRegexp = /^(https?:\/\/)([^/]+)(\/.*)?/;
const copyButtonLooseRightOffset = 43;
const websiteRowElementName = 'websiteRow';
const secondaryClassName = 'ws-url-secondary';

const onRowResize: OnResize<{ setIsTooTight: React.Dispatch<React.SetStateAction<boolean>> }> = (entry, ctx) => {
  const parentWidth = entry.contentRect.width;
  const row = entry.target.children.namedItem(websiteRowElementName);
  let contentWidth = row?.getClientRects()[0]?.width!;
  if (!contentWidth || !parentWidth) return;

  ctx.setIsTooTight((prevIsTooTight) => {
    if (prevIsTooTight) contentWidth += copyButtonLooseRightOffset;
    let remainingWidth = parentWidth - contentWidth - 2;
    const isTooTight = remainingWidth <= 0;
    return isTooTight;
  });
};

export const WebsiteUrlRow = ({ url, component = 'div', showProtocol = false }: WebsiteRowProps) => {
  const [, protocol, origin, pathname] = url?.match(urlRegexp) ?? [];
  const containerRef = useRef<HTMLDivElement>(null);
  const [isTooTight, setIsTooTight] = useState(false);
  const theme = useTheme();
  const secondaryColor = theme.palette.grey['700'];
  useElementResized(containerRef.current?.parentElement, {
    cb: onRowResize,
    ctx: { setIsTooTight },
  });

  const handleCopyClick = () => url && copyTextToClipboard(url);

  const handleDoubleClick: React.MouseEventHandler<HTMLDivElement> = (e) =>
    !(e.target as HTMLElement).closest?.(`.${iconButtonClasses.root}`) && selectElementContent(e.currentTarget);

  const handleCopy: React.ClipboardEventHandler<HTMLDivElement> = (e) => {
    const selectedText = window.getSelection()?.toString();
    if (url && selectedText === `${showProtocol ? protocol : ''}\n${origin}\n${pathname}`) {
      e.preventDefault();
      copyTextToClipboard(url);
    }
  };

  return (
    <StyledTooltip title={url} disableHoverListener={!isTooTight} arrow placement="top-end">
      <Stack
        component={component}
        ref={containerRef}
        name={websiteRowElementName}
        style={{
          flexGrow: 0,
          width: 'fit-content',
          maxWidth: '100%',
        }}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 0.25,
          position: 'relative',
          fontWeight: 'bold',
          fontFamily: theme.typography.fontFamily,
          fontSize: component === 'h3' ? '20px' : '16px',
          lineHeight: component === 'h3' ? '28px' : '24px',
          maxHeight: component === 'h3' ? '28px' : '24px',
          margin: 0,
          [`& .${iconButtonClasses.root}`]: {
            opacity: 0,
            transition: 'opacity 0.25s ease-in-out, background-color 0.25s ease-in-out',
          },
          '&:hover': {
            [`& .${iconButtonClasses.root}`]: {
              opacity: 1,
            },
            [`& .${secondaryClassName}`]: {
              color: theme.palette.text.primary,
            },
          },
        }}
        onDoubleClick={handleDoubleClick}
        onCopy={handleCopy}
      >
        {protocol ? (
          <>
            <Typography
              component="span"
              variant="inherit"
              className={secondaryClassName}
              style={
                showProtocol
                  ? { whiteSpace: 'nowrap', flexShrink: 0, flexGrow: 0, minWidth: 0 }
                  : {
                      // flex: '0 0 0',
                      overflow: 'hidden',
                      width: 0,
                    }
              }
              sx={{
                color: secondaryColor,
              }}
            >
              {protocol}
            </Typography>

            <Typography
              color="textPrimary"
              component="span"
              variant="inherit"
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flexShrink: 1,
                flexGrow: 0,
                minWidth: 0,
              }}
            >
              {origin}
            </Typography>

            <Typography
              component="span"
              variant="inherit"
              className={secondaryClassName}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                flexShrink: 4,
                flexGrow: 0,
                minWidth: 0,
              }}
              sx={{
                color: secondaryColor,
              }}
            >
              {pathname}
            </Typography>
          </>
        ) : (
          <Typography
            color="textPrimary"
            component="span"
            variant="inherit"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              flexShrink: 1,
              flexGrow: 0,
              minWidth: 0,
            }}
          >
            {url}
          </Typography>
        )}
        <IconButton
          size="small"
          onClick={handleCopyClick}
          sx={{
            position: isTooTight ? 'absolute' : 'relative',
            right: isTooTight ? '-12px' : ``,
            background: 'linear-gradient(to right, #fff3, #fffb 35%, #fffb)',
            '&:hover': { background: '#fff' },
          }}
        >
          <ContentCopy fontSize="small" />
        </IconButton>
      </Stack>
    </StyledTooltip>
  );
};
